import { z } from 'zod';
import { Bet } from './bet.ts';
import { CrossingEvent } from './crossing.ts';
import { CameraHealth } from './game.ts';
import { Balance } from './wallet.ts';
import { GameRoundId, UtcInstant } from './primitives.ts';
import { RoundSnapshot, RoundState } from './round.ts';

export const PROTOCOL_VERSION = 1 as const;

/** Public round topic: never carries player or wallet data. */
export const PublicEventPayload = z.discriminatedUnion('type', [
  z.object({ type: z.literal('round.snapshot'), round: RoundSnapshot }),
  z.object({ type: z.literal('round.state'), from: RoundState, to: RoundState, reason: z.string().max(256).nullable() }),
  z.object({ type: z.literal('round.crossing'), event: CrossingEvent, count: z.number().int().nonnegative() }),
  z.object({
    type: z.literal('round.result'),
    finalCount: z.number().int().nonnegative(),
    winningSelections: z.array(z.string()),
  }),
  /** Live-camera round: every client plays the locked round media from startAt; the result follows at revealAt. */
  z.object({ type: z.literal('round.playback'), assetId: z.string().nullable(), startAt: z.string(), revealAt: z.string() }),
  z.object({ type: z.literal('source.health'), health: CameraHealth }),
]);
export type PublicEventPayload = z.infer<typeof PublicEventPayload>;

/** Private player topic: scoped to one authenticated player session. */
export const PrivateEventPayload = z.discriminatedUnion('type', [
  z.object({ type: z.literal('bet.updated'), bet: Bet }),
  z.object({ type: z.literal('balance.changed'), balance: Balance }),
  z.object({ type: z.literal('session.expired') }),
  /** Inventory, level or check-in changed; clients re-read the rewards view. */
  z.object({ type: z.literal('rewards.changed') }),
]);
export type PrivateEventPayload = z.infer<typeof PrivateEventPayload>;

const envelope = <T extends z.ZodType>(payload: T) =>
  z.object({
    protocolVersion: z.literal(PROTOCOL_VERSION),
    eventId: z.string().min(1).max(128),
    /** Strictly increasing per topic; a gap requires a fresh snapshot. */
    sequence: z.number().int().nonnegative(),
    occurredAt: UtcInstant,
    gameRoundId: GameRoundId.nullable(),
    payload,
  });

export const PublicEventEnvelope = envelope(PublicEventPayload);
export type PublicEventEnvelope = z.infer<typeof PublicEventEnvelope>;
export const PrivateEventEnvelope = envelope(PrivateEventPayload);
export type PrivateEventEnvelope = z.infer<typeof PrivateEventEnvelope>;
